import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { StepUpCancelled, type StepUpCredential, type StepUpMethod } from '@akaal/jade-ui'

import * as authApi from '@/api/auth'
import type { AuthToken, InviteAcceptance, MeUser, MfaSetup } from '@/api/auth'
import { ApiError, isRefusal } from '@/api/client'
import * as ssoApi from '@/api/sso'
import {
  blockTenantWrites,
  clearToken,
  setToken,
  tenantReadOnly,
  token,
  tokenStage,
} from '@/api/session'
import type { WriteGuard } from '@/composables/guardedWrite'
import { disconnectEcho } from '@/live/echo'
import { identify } from '@/observability'
import { useBrandStore } from '@/stores/brand'

export type MfaStage = 'setup_required' | 'verify_required' | 'verified'

/**
 * The permissions a read-only tenant keeps: reads, and the billing writes it
 * needs to fix the card. Mirrors the API's `EnsureTenantWritable` allow-list
 * (auth, billing and notification preferences), of which only billing carries
 * a permission string.
 */
export function isWritePermission(permission: string): boolean {
  return !permission.endsWith('.read') && permission !== 'billing.manage'
}

/**
 * Who is signed in, and what they may do.
 *
 * There is no refresh endpoint, and none is needed: the API issues a 12-hour
 * sliding token (D57) that every call extends, so a session ends only by
 * standing idle, by `signOut`, or by `signOutEverywhere` from any browser.
 * The console proves a stored token is still good by calling `/auth/me` on
 * load. That call is also how permissions, the effective timezone and any
 * impersonation reach the rest of the app, so it runs before the first route
 * resolves.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<MeUser | null>(null)
  const mfaStage = ref<MfaStage | null>(null)
  /** False until restore() has settled, so a guard never redirects too early. */
  const ready = ref(false)

  const isAuthenticated = computed(() => token.value !== null && user.value !== null)
  const isImpersonating = computed(() => user.value?.impersonation != null)
  const impersonatedTenantName = computed(() => user.value?.impersonation?.tenant_name ?? null)
  const permissions = computed(() => new Set(user.value?.permissions ?? []))
  /**
   * The channel live updates arrive on (MRS 6.15): the tenant this session
   * reads, which under impersonation is the viewed tenant (D127). Echo adds
   * the `private-` prefix. Null with no tenant, and then nothing subscribes.
   */
  const liveChannel = computed(() => {
    const tenantId = user.value?.impersonation?.tenant_id ?? user.value?.tenant_id ?? null
    return tenantId === null ? null : `tenant.${tenantId}`
  })
  /** Whether the tenant has the Device Agent (MRS 6.14); every agent feature hides when not. */
  const agentEnabled = computed(() => user.value?.agent_enabled === true)
  /**
   * Where a tester downloads the Device Agent APK while the deployment
   * distributes the agent by sideload (D122); null under managed Play, with
   * the URL unset, or for a tenant without the agent.
   */
  const agentDownloadUrl = computed(() => user.value?.agent_download_url ?? null)
  /** Whether the operator white-labelled the tenant (#222): the Branding tab shows only then. */
  const whiteLabel = computed(() => user.value?.white_label === true)
  /**
   * A coarse clock, so `stepUpFresh` lapses on its own rather than only when
   * the user object changes. Fifteen seconds is fine: the window is five
   * minutes, and the API is the judge either way.
   */
  const now = ref(Date.now())
  setInterval(() => {
    now.value = Date.now()
  }, 15_000)
  /** Whether a destructive call would pass the API's five-minute step-up check right now. */
  const stepUpFresh = computed(() => {
    const until = user.value?.step_up_valid_until
    return until != null && Date.parse(until) > now.value
  })
  /**
   * How this session may re-open the window (D91), for the step-up dialog.
   * The API always lists `password`, so an empty list only ever means no user
   * (the layout outlives a sign-out by a tick); the dialog refuses an empty
   * list, so the password stands in rather than the whole shell failing.
   */
  const stepUpMethods = computed<StepUpMethod[]>(() => {
    const methods = user.value?.step_up_methods ?? []
    return methods.length === 0 ? ['password'] : methods
  })

  /** Whether the session holds the permission at all: what decides which pages and tabs open. */
  function has(permission: string): boolean {
    return permissions.value.has(permission)
  }

  /**
   * Whether the session may act on the permission right now. A read-only
   * tenant (MRS 6.10) keeps every read and its billing writes, so it can pay;
   * everything else the API would refuse with a 402, and a control that only
   * ever fails is worse than none. Views gate their write controls on this,
   * the router and the nav on `has`.
   */
  function can(permission: string): boolean {
    if (!has(permission)) return false
    return !tenantReadOnly.value || !isWritePermission(permission)
  }

  // The API client drops a rejected token on its own (see api/index.ts);
  // everything that hung off it goes here, so no stale user survives.
  watch(token, (value) => {
    if (value === null && user.value !== null) forget()
  })

  function forget(): void {
    user.value = null
    mfaStage.value = null
    clearToken()
    identify(null)
    // The socket was authorised with the token that just went (MRS 6.15):
    // closed here, so every way out of a session closes it.
    disconnectEcho()
    // The next person at this browser must not see the last tenant's brand.
    useBrandStore().reset()
  }

  async function loadUser(): Promise<void> {
    user.value = await authApi.fetchMe()
    // The API says up front when the tenant is read-only (MRS 6.10, D59), so
    // every role loses its write controls before a first refused write says
    // so. Nothing lifts the flag here: `clearToken` resets it, and a
    // subscription read is the only in-session authority the other way.
    if (user.value.subscription_state === 'read_only') blockTenantWrites()
    mfaStage.value = 'verified'
    identify({ id: user.value.id, email: user.value.email })
    // `/auth/me` is the only brand source after sign-in (#222).
    useBrandStore().applyFromMe(user.value)
  }

  /** Keeps a token the API just issued and finishes loading who it belongs to. */
  async function adopt(issued: { token: string; mfa: MfaStage }): Promise<void> {
    setToken(issued.token, issued.mfa)
    mfaStage.value = issued.mfa
    await loadUser()
  }

  async function restore(): Promise<void> {
    if (token.value === null) {
      ready.value = true
      return
    }
    // A reload in the middle of MFA: the token cannot open the console yet and
    // `/auth/me` would only say so. Put the person back on the page they left.
    if (tokenStage.value === 'setup_required' || tokenStage.value === 'verify_required') {
      mfaStage.value = tokenStage.value
      ready.value = true
      return
    }
    try {
      await loadUser()
    } catch {
      // Expired, revoked, or issued before an MFA step. Either way this token
      // cannot open the console, so drop it rather than retry on every route.
      forget()
    } finally {
      ready.value = true
    }
  }

  /**
   * Takes a token handed over by the operator panel and makes it this
   * browser's session. Whatever was here before goes first, so an operator's
   * own console session can never bleed into the tenant view. Only a token
   * the API reports as an impersonation is kept: this is a viewer hand-off,
   * not a second way to sign in.
   */
  async function adoptToken(value: string): Promise<void> {
    forget()
    setToken(value, 'verified')
    mfaStage.value = 'verified'
    try {
      await loadUser()
    } catch (error) {
      forget()
      throw error
    }
    if (user.value?.impersonation == null) {
      forget()
      throw new Error('The handed-over token is not an impersonation token.')
    }
  }

  /**
   * Keeps a token a sign-in just earned. setup_required and verify_required
   * tokens may only call the MFA endpoints, so asking for the user then
   * would just earn a 403: the user is loaded only once the API says the
   * token is fully verified.
   */
  async function keep(issued: AuthToken): Promise<MfaStage> {
    setToken(issued.token, issued.mfa)
    mfaStage.value = issued.mfa
    if (issued.mfa === 'verified') await loadUser()
    return issued.mfa
  }

  async function signIn(email: string, password: string): Promise<MfaStage> {
    return keep(await authApi.login(email, password))
  }

  /** An accepted invitation earns a token exactly like a fresh sign-in. */
  async function acceptInvite(body: InviteAcceptance): Promise<MfaStage> {
    return keep(await authApi.acceptInvite(body))
  }

  /**
   * What the identity provider sent back, exchanged for a session (MRS 3.1).
   * Whatever this browser held goes first, so a half-finished password
   * sign-in can never survive alongside the one the provider just proved.
   */
  async function signInWithSso(state: string, code: string): Promise<MfaStage> {
    forget()
    return keep(await authApi.completeSso(state, code))
  }

  /**
   * Finishes a sign-in with a code from the authenticator or a recovery
   * code. A code accepted once already swapped the token for a full one; if
   * only the `/auth/me` after it failed, the page never moved on and every
   * retry earns 403 `mfa_token_required` (saas-admin #48). For a stored
   * stage of `verified` that refusal means the person is in, so the user is
   * loaded and the page carries on; a load that fails then drops the token,
   * since nothing else can. A real pre-MFA token never gets this code, so
   * with any other stage the refusal reaches the page unchanged.
   */
  async function finishMfa(issue: () => Promise<AuthToken>): Promise<void> {
    let issued: AuthToken
    try {
      issued = await issue()
    } catch (error) {
      if (!isRefusal(error, 403, 'mfa_token_required') || tokenStage.value !== 'verified') {
        throw error
      }
      try {
        await loadUser()
      } catch (loadError) {
        forget()
        throw loadError
      }
      return
    }
    await adopt(issued)
  }

  async function confirmMfa(code: string): Promise<void> {
    await finishMfa(() => authApi.verifyMfa(code))
  }

  /** A recovery code stands in for the authenticator once, then is spent. */
  async function recover(code: string): Promise<void> {
    await finishMfa(() => authApi.recover(code))
  }

  /**
   * First-time enrolment. Call once per screen: every call replaces the
   * unconfirmed secret, so a second one would orphan a QR already scanned.
   */
  async function beginMfaSetup(): Promise<MfaSetup> {
    return authApi.mfaSetup()
  }

  /**
   * Proves the authenticator works and returns the recovery codes. They are
   * returned rather than stored: the screen shows them once and they are gone.
   */
  async function completeMfaSetup(code: string): Promise<string[]> {
    const confirmed = await authApi.mfaConfirm(code)
    await adopt(confirmed)
    return confirmed.recovery_codes
  }

  /**
   * Re-authenticates for the step-up dialog. Answers whether the credential
   * was accepted; a wrong one is an answer, not an error, which is the shape
   * the library's dialog expects from its `verify` callback.
   */
  async function stepUp(credential: StepUpCredential): Promise<boolean> {
    try {
      const opened = await authApi.stepUp(
        credential.kind === 'password'
          ? { password: credential.value }
          : { code: credential.value },
      )
      openStepUpWindow(opened.step_up_valid_until)
      return true
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 422)) return false
      throw error
    }
  }

  /** The API opened the window; `stepUpFresh` must say so at once, not at the next tick of the clock. */
  function openStepUpWindow(until: string): void {
    if (user.value !== null) user.value = { ...user.value, step_up_valid_until: until }
    now.value = Date.now()
  }

  /**
   * Finishes a step-up through the identity provider (D91): the state and
   * code the provider sent back to the callback page open the window. A
   * refusal (422 on `credentials`, 503 `idp_unavailable`) is thrown for the
   * page to explain; unlike a typed credential there is nothing to retype.
   */
  async function completeSsoStepUp(state: string, code: string): Promise<void> {
    const opened = await ssoApi.completeSsoStepUp(state, code)
    openStepUpWindow(opened.step_up_valid_until)
  }

  /**
   * The provider refused to confirm this session (409 `sso_not_linked`), so
   * the dialog stops offering it until `/auth/me` is read again and says
   * otherwise. The other methods stay.
   */
  function withdrawSsoStepUp(): void {
    if (user.value === null) return
    user.value = {
      ...user.value,
      step_up_methods: user.value.step_up_methods.filter((method) => method !== 'sso'),
    }
  }

  /**
   * Saves the signed-in person's own name or profile time zone (null hands
   * the zone back to the tenant's). The API answers with the whole user, so
   * the top bar and every rendered time follow at once, with no reload.
   */
  async function updateProfile(changes: {
    name?: string
    timezone?: string | null
  }): Promise<void> {
    if (user.value === null) throw new Error('updateProfile needs a signed-in user.')
    user.value = await authApi.updateMe(changes)
  }

  /**
   * Throws away a token that never finished multi-factor authentication.
   *
   * Without this a user who abandons the code screen is stuck: the guard sends
   * them back to it on every navigation, and the half-authenticated token sits
   * in storage across reloads with no way to discard it. No revoke call,
   * because a pre-MFA token may only reach the MFA endpoints.
   */
  function abandonSignIn(): void {
    forget()
  }

  async function signOut(): Promise<void> {
    try {
      await authApi.logout()
    } catch {
      // A token that cannot be revoked is still a token this browser must
      // stop using. Server-side expiry catches the rest.
    } finally {
      forget()
    }
  }

  /**
   * Ends every session this person holds, this browser's included (D57).
   * The API insists on a fresh step-up, so the call runs inside the guard
   * the shell supplies; the token is forgotten only once the guard has
   * finished, so the guard's retry never runs without one. A cancelled
   * prompt changes nothing and answers null. A refused call still ends this
   * browser's session, as `signOut` does, and is rethrown so the caller can
   * say the others may still be live. Otherwise answers how many tokens went.
   */
  async function signOutEverywhere(guard: WriteGuard): Promise<number | null> {
    let revoked: number
    try {
      revoked = (await guard(authApi.logoutAll)).revoked
    } catch (error) {
      if (error instanceof StepUpCancelled) return null
      forget()
      throw error
    }
    forget()
    return revoked
  }

  return {
    user,
    mfaStage,
    ready,
    isAuthenticated,
    isImpersonating,
    impersonatedTenantName,
    liveChannel,
    stepUpFresh,
    stepUpMethods,
    agentEnabled,
    agentDownloadUrl,
    whiteLabel,
    has,
    can,
    restore,
    adoptToken,
    signIn,
    acceptInvite,
    signInWithSso,
    confirmMfa,
    recover,
    beginMfaSetup,
    completeMfaSetup,
    stepUp,
    completeSsoStepUp,
    withdrawSsoStepUp,
    updateProfile,
    abandonSignIn,
    signOut,
    signOutEverywhere,
  }
})
